import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUrlParam, useUrlPanelSync } from '../hooks/useUrlParam';
import { Link, useNavigate } from 'react-router-dom';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import Sidebar from '../components/layout/Sidebar';
import HostingNav from '../components/layout/HostingNav';
import PageHeader from '../components/PageHeader';
import { Badge, Button, Card, Dropdown, Input, KebabMenu, SearchableSelect, SidePanel, Table, Tabs, useToast } from '../components/ui';
import type { BadgeTone, TableColumn } from '../components/ui';
import RunStatusBadge from '../components/hosting/RunStatusBadge';
import HostedSitesEditor from '../components/hosting/HostedSitesEditor';
import OpenFixBadge from '../components/hosting/OpenFixBadge';
import { useUser } from '../context/UserContext';
import { timeAgo } from '../lib/comms';
import { supabase } from '../lib/supabase';
import {
  HOSTING_STATUS_LABEL,
  deleteHostingAccount,
  fetchHostingAccounts,
  fetchLatestRuns,
  fetchMonitoredSites,
  isLiveRun,
  isRunnableSite,
  queueHostingRun,
  saveHostingAccount,
  type HostedSite,
  type HostingAccount,
  type HostingRun,
  type HostingAccountInput,
  type HostingStatus,
} from '../lib/hosting';
import { enqueueHostingFix, fetchOpenFixJobsBySite, fetchPendingFixes, type PendingFix } from '../lib/hostingFixPlan';
import type { AiJob } from '../lib/aiJobs';

const STATUS_TONE: Record<HostingStatus, BadgeTone> = { active: 'up', paused: 'warn', cancelled: 'neutral' };
const RUN_POLL_MS = 4000;
const TABS: Array<{ value: HostingStatus | 'all'; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'all', label: 'All' },
];

const EMPTY: HostingAccountInput = {
  name: '',
  status: 'active',
  client_id: null,
  contact_name: null,
  contact_email: null,
  provider: null,
  plan: null,
  monthly_price: null,
  started_on: null,
  notes: null,
};

const money = (n: number | null) =>
  n === null ? '—' : n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
// An account with a hosting fix still in the agent queue gets this wash, like Pages rows with a job working.
const OPEN_FIX_WASH: React.CSSProperties = { background: 'var(--status-warn-soft)' };
// The list names this many of an account's sites, then "+N more".
const SITES_SHOWN = 2;
const host = (url: string) => url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
const blank = (s: string) => (s.trim() ? s.trim() : null);

/**
 * Managed hosting: every hosting customer we run, separate from client accounts
 * but attachable to one. Websites are uptime-monitor rows; attaching one or
 * adding a new one here is the same table the Uptime page and uptime-ping read.
 */
export default function Hosting() {
  const [accounts, setAccounts] = useState<HostingAccount[]>([]);
  const [sites, setSites] = useState<HostedSite[]>([]);
  const [clients, setClients] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState<HostingStatus | 'all'>('active');
  const [search, setSearch] = useState('');

  const [panelOpen, setPanelOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<HostingAccountInput>(EMPTY);
  const [priceText, setPriceText] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const [latestRuns, setLatestRuns] = useState<Record<string, HostingRun>>({});
  const [openFixes, setOpenFixes] = useState<Record<string, AiJob>>({});
  const [queuingId, setQueuingId] = useState<string | null>(null);
  const [pendingFixes, setPendingFixes] = useState<Record<string, PendingFix>>({});
  const [sendingFixId, setSendingFixId] = useState<string | null>(null);

  const navigate = useNavigate();
  const { user } = useUser();
  const toast = useToast();

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [a, s, c] = await Promise.all([
        fetchHostingAccounts(),
        fetchMonitoredSites(),
        supabase.from('clients').select('id, name').order('name'),
      ]);
      setAccounts(a);
      setSites(s);
      setClients(c.data || []);
    } catch (err) {
      console.error('Error loading hosting accounts:', err);
      setLoadError(err instanceof Error ? err.message : 'Failed to load hosting accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Last runs load on their own: the list still works if they can't.
  const loadRuns = useCallback(async () => {
    try {
      setLatestRuns(await fetchLatestRuns());
    } catch (err) {
      console.error('Error loading hosting runs:', err);
    }
  }, []);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  // Open hosting fix jobs load on their own too: no highlight is better than no list.
  const loadOpenFixes = useCallback(() => {
    fetchOpenFixJobsBySite()
      .then(setOpenFixes)
      .catch((err) => console.error('Error loading open hosting fixes:', err));
  }, []);

  useEffect(() => {
    loadOpenFixes();
  }, [loadOpenFixes]);

  // Fixes waiting to be sent, from each account's latest finished run. Keyed on the
  // runs' ids and statuses so the live-run poll doesn't refetch while nothing changed.
  const runsKey = Object.values(latestRuns).map((r) => `${r.id}:${r.status}`).sort().join(',');
  const loadPendingFixes = useCallback(async () => {
    try {
      setPendingFixes(await fetchPendingFixes(latestRuns, sites));
    } catch (err) {
      console.error('Error loading hosting fixes to send:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runsKey, sites]);

  useEffect(() => {
    loadPendingFixes();
  }, [loadPendingFixes]);

  // Keep Queued / Running badges moving until every run has finished.
  const anyLive = Object.values(latestRuns).some(isLiveRun);
  useEffect(() => {
    if (!anyLive) return;
    const timer = window.setInterval(loadRuns, RUN_POLL_MS);
    return () => window.clearInterval(timer);
  }, [anyLive, loadRuns]);

  const clientName = useCallback((id: string | null) => clients.find((c) => c.id === id)?.name ?? null, [clients]);
  const sitesFor = useCallback((id: string) => sites.filter((s) => s.hosting_account_id === id), [sites]);
  const hasOpenFix = useCallback((id: string) => sitesFor(id).some((s) => openFixes[s.id]), [sitesFor, openFixes]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return accounts
      .filter((a) => tab === 'all' || a.status === tab)
      .filter(
        (a) =>
          !term ||
          a.name.toLowerCase().includes(term) ||
          (clientName(a.client_id) || '').toLowerCase().includes(term) ||
          sitesFor(a.id).some((s) => s.url.toLowerCase().includes(term) || s.name.toLowerCase().includes(term))
      );
  }, [accounts, tab, search, clientName, sitesFor]);

  const activeCount = accounts.filter((a) => a.status === 'active').length;
  const activeMrr = accounts.filter((a) => a.status === 'active').reduce((sum, a) => sum + (a.monthly_price || 0), 0);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setPriceText('');
    setFormError(null);
    setPanelOpen(true);
  };

  const openEdit = (a: HostingAccount) => {
    setEditingId(a.id);
    setForm({
      name: a.name,
      status: a.status,
      client_id: a.client_id,
      contact_name: a.contact_name,
      contact_email: a.contact_email,
      provider: a.provider,
      plan: a.plan,
      monthly_price: a.monthly_price,
      started_on: a.started_on,
      notes: a.notes,
    });
    setPriceText(a.monthly_price === null ? '' : String(a.monthly_price));
    setFormError(null);
    setPanelOpen(true);
  };

  // The account being edited is ?edit=<id> (the detail page's Edit button links here the same way), so refresh
  // and back keep the panel; a new account's form stays local.
  const [editParam, setEditParam] = useUrlParam('edit');
  useUrlPanelSync(
    editParam,
    panelOpen ? editingId : null,
    !loading,
    (id) => {
      const target = accounts.find((a) => a.id === id);
      if (target) openEdit(target);
    },
    () => setPanelOpen(false),
  );
  const closePanel = () => {
    setPanelOpen(false);
    if (editParam) setEditParam(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setFormError('Give the account a name');
      return;
    }
    const price = priceText.trim() ? Number(priceText.replace(/[$,\s]/g, '')) : null;
    if (price !== null && Number.isNaN(price)) {
      setFormError('Monthly price must be a number');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const saved = await saveHostingAccount(editingId, { ...form, name: form.name.trim(), monthly_price: price });
      await load();
      // A new account stays open so its sites can be attached straight away.
      setEditingId(saved.id);
      // A new account stays open for its sites; it now has an id, so it gets a URL like any edit.
      setEditParam(saved.id);
    } catch (err) {
      console.error('Error saving hosting account:', err);
      setFormError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteHostingAccount(id);
      setConfirmDeleteId(null);
      await load();
    } catch (err) {
      console.error('Error deleting hosting account:', err);
      setLoadError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleRun = async (a: HostingAccount) => {
    setQueuingId(a.id);
    try {
      const run = await queueHostingRun(a.id, { updates: true }, user?.name ?? null);
      setLatestRuns((prev) => ({ ...prev, [a.id]: run }));
      toast.success('Checklist queued', a.name);
    } catch (err) {
      console.error('Error queuing hosting run:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to queue the run', a.name);
      loadRuns();
    } finally {
      setQueuingId(null);
    }
  };

  // Same as the run's Fix plan button: one spec job per site with agent-fixable findings.
  const handleSendFix = async (a: HostingAccount) => {
    const pending = pendingFixes[a.id];
    if (!pending || !user?.id) return;
    setSendingFixId(a.id);
    const createdBy = { id: user.id, name: user.name || user.email || 'Someone' };
    let sent = 0;
    try {
      for (const site of pending.sites) {
        await enqueueHostingFix({ account: a, clientName: clientName(a.client_id), run: pending.run, site, steps: pending.steps, items: pending.items, createdBy });
        sent += 1;
      }
      toast.success(sent === 1 ? 'Hosting fix sent to the agent queue' : `${sent} hosting fixes sent to the agent queue`, a.name);
    } catch (err) {
      console.error('Error queuing hosting fix:', err);
      toast.error(err instanceof Error ? err.message : 'Could not queue the fixes', a.name);
    } finally {
      setSendingFixId(null);
      loadOpenFixes();
      loadPendingFixes();
    }
  };

  // The drawer's Websites editor changes sites only; their fix badges may move with them.
  const reloadSites = useCallback(async () => {
    try {
      setSites(await fetchMonitoredSites());
    } catch (err) {
      console.error('Error reloading hosted sites:', err);
    }
    loadOpenFixes();
  }, [loadOpenFixes]);

  // Shared by the desktop table's last column and the phone cards.
  const rowActions = (a: HostingAccount) =>
    confirmDeleteId === a.id ? (
      <span style={{ display: 'inline-flex', gap: '6px' }} onClick={(e) => e.stopPropagation()}>
        <Button size="sm" variant="danger" onClick={() => handleDelete(a.id)}>
          Delete
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setConfirmDeleteId(null)}>
          Keep
        </Button>
      </span>
    ) : (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }} onClick={(e) => e.stopPropagation()}>
        <RunCell
          sites={sitesFor(a.id)}
          live={isLiveRun(latestRuns[a.id])}
          busy={queuingId === a.id}
          onRun={() => handleRun(a)}
        />
        <FixCell
          pending={pendingFixes[a.id]}
          live={isLiveRun(latestRuns[a.id])}
          hasRun={Boolean(latestRuns[a.id])}
          inQueue={hasOpenFix(a.id)}
          signedIn={Boolean(user?.id)}
          busy={sendingFixId === a.id}
          onSend={() => handleSendFix(a)}
        />
        <KebabMenu
          actions={[
            { label: 'Edit', icon: faPen, onClick: () => setEditParam(a.id) },
            { label: 'Delete', icon: faTrash, danger: true, onClick: () => setConfirmDeleteId(a.id) },
          ]}
        />
      </span>
    );

  const columns: TableColumn<HostingAccount>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Account',
        width: '22%',
        render: (_, a) => (
          <div style={{ minWidth: 0 }}>
            <div style={{ color: 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.name}</div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {clientName(a.client_id) ?? 'No client attached'}
              {siteCount(sitesFor(a.id))}
            </div>
          </div>
        ),
      },
      {
        key: 'sites',
        label: 'Websites',
        width: '19%',
        wrap: true,
        render: (_, a) => <SiteList sites={sitesFor(a.id)} openFixes={openFixes} />,
      },
      {
        key: 'provider',
        label: 'Provider',
        width: '19%',
        // Provider over plan, like the Account cell: plan names run long
        // ("Webmaster Services - Outpost") and a nowrap cell spills into Monthly.
        render: (_, a) => (
          <div style={{ minWidth: 0 }}>
            <div style={{ color: 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.provider || '—'}</div>
            {a.plan ? (
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis' }} title={a.plan}>
                {a.plan}
              </div>
            ) : null}
          </div>
        ),
      },
      { key: 'monthly_price', label: 'Monthly', align: 'right', mono: true, width: '9%', render: (_, a) => <span>{money(a.monthly_price)}</span> },
      {
        key: 'status',
        label: 'Status',
        width: '10%',
        render: (_, a) => (
          <Badge tone={STATUS_TONE[a.status]} dot>
            {HOSTING_STATUS_LABEL[a.status]}
          </Badge>
        ),
      },
      {
        key: 'last_run',
        label: 'Last run',
        width: '12%',
        render: (_, a) => {
          const r = latestRuns[a.id];
          if (!r) return <span style={{ color: 'var(--text-faint)' }}>—</span>;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '3px' }}>
              <RunStatusBadge
                status={r.status}
                reviewed={Boolean(r.reviewed_at)}
                title={r.reviewed_at ? `Reviewed by ${r.reviewed_by_name || 'someone'}${r.review_note ? `: ${r.review_note}` : ''}. ${r.summary ?? ''}`.trim() : (r.summary ?? undefined)}
              />
              <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                {timeAgo(r.finished_at ?? r.started_at ?? r.created_at)}
              </span>
            </div>
          );
        },
      },
      {
        key: 'actions',
        label: '',
        width: '220px',
        align: 'right',
        render: (_, a) => rowActions(a),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientName, sitesFor, confirmDeleteId, latestRuns, queuingId, openFixes, pendingFixes, sendingFixId]
  );

  const editing = editingId ? accounts.find((a) => a.id === editingId) ?? null : null;

  return (
    <div className="min-h-screen">
      <Sidebar />

      <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
        <PageHeader
          title="Hosting"
          count={activeCount}
          searchPlaceholder="Search accounts, clients, sites…"
          searchValue={search}
          onSearchChange={setSearch}
          actions={
            <Button size="sm" icon="plus" onClick={openCreate}>
              New Hosting Account
            </Button>
          }
        />

        <HostingNav />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: 'var(--space-5)' }}>
          <Tabs
            tabs={TABS.map((t) => t.label)}
            active={TABS.find((t) => t.value === tab)?.label ?? 'Active'}
            onChange={(label) => {
              const def = TABS.find((t) => t.label === label);
              if (def) setTab(def.value);
            }}
          />
          <span className="t-num" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
            {activeCount} active · {money(activeMrr)}/mo
          </span>
        </div>

        {loadError ? (
          <Card>
            <div style={{ color: 'var(--status-down)', fontSize: 'var(--text-sm)' }}>{loadError}</div>
          </Card>
        ) : loading ? null : visible.length === 0 ? (
          <Card>
            <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
              {accounts.length === 0 ? 'No hosting accounts yet. Add the first one.' : 'Nothing matches.'}
            </div>
          </Card>
        ) : (
          <>
            {/* Phones: a card per account. The seven-column table only showed
                Account and Websites without a sideways scroll. */}
            <div
              className="md:hidden"
              style={{
                background: 'var(--surface-card)',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-rest)',
                overflow: 'hidden',
              }}
            >
              {visible.map((a, i) => (
                <AccountCard
                  key={a.id}
                  account={a}
                  first={i === 0}
                  client={clientName(a.client_id)}
                  sites={sitesFor(a.id)}
                  run={latestRuns[a.id]}
                  openFixes={openFixes}
                  onOpen={() => navigate(`/hosting/${a.id}`)}
                  actions={rowActions(a)}
                />
              ))}
            </div>
            {/* Seven columns: scroll sideways rather than crush them. */}
            <div className="hidden md:block" style={{ overflowX: 'auto' }}>
              <Table
                columns={columns}
                rows={visible}
                onRowClick={(a) => navigate(`/hosting/${a.id}`)}
                rowStyle={(a) => (hasOpenFix(a.id) ? OPEN_FIX_WASH : undefined)}
                style={{ minWidth: '1124px' }}
              />
            </div>
          </>
        )}
      </main>

      <SidePanel
        open={panelOpen}
        onClose={closePanel}
        eyebrow="Managed hosting"
        title={editingId ? form.name || 'Hosting account' : 'New Hosting Account'}
        width={480}
        footer={
          <>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editingId ? 'Save changes' : 'Create'}
            </Button>
            <Button variant="ghost" onClick={closePanel} disabled={saving}>
              Close
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {formError ? <ErrorNote text={formError} /> : null}

          <Input label="Account name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Julien Kibler Photography" />

          <SearchableSelect
            label="Client account"
            placeholder="Not attached to a client"
            searchPlaceholder="Search clients…"
            clearLabel="Not attached to a client"
            allowClear
            options={clients.map((c) => ({ value: c.id, label: c.name }))}
            value={form.client_id}
            onChange={(v) => setForm((f) => ({ ...f, client_id: v }))}
          />

          <Dropdown
            label="Status"
            options={(Object.keys(HOSTING_STATUS_LABEL) as HostingStatus[]).map((s) => ({ value: s, label: HOSTING_STATUS_LABEL[s] }))}
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as HostingStatus }))}
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Input label="Provider" value={form.provider ?? ''} onChange={(e) => setForm((f) => ({ ...f, provider: blank(e.target.value) }))} placeholder="e.g. Cloudways" />
            <Input label="Plan" value={form.plan ?? ''} onChange={(e) => setForm((f) => ({ ...f, plan: blank(e.target.value) }))} placeholder="e.g. Managed WordPress" />
            <Input label="Monthly price" value={priceText} onChange={(e) => setPriceText(e.target.value)} placeholder="e.g. 49" />
            <Input label="Started" type="date" value={form.started_on ?? ''} onChange={(e) => setForm((f) => ({ ...f, started_on: e.target.value || null }))} />
            <Input label="Contact name" value={form.contact_name ?? ''} onChange={(e) => setForm((f) => ({ ...f, contact_name: blank(e.target.value) }))} />
            <Input label="Contact email" value={form.contact_email ?? ''} onChange={(e) => setForm((f) => ({ ...f, contact_email: blank(e.target.value) }))} />
          </div>

          <div>
            <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Notes</div>
            <textarea
              className="ck-textarea"
              rows={3}
              value={form.notes ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value || null }))}
              placeholder="Anything worth knowing about this account"
            />
          </div>

          <div style={{ borderTop: '1px solid var(--border-hairline)', paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div className="t-eyebrow">Websites · watched by the uptime monitor</div>
            {!editing ? (
              <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>Create the account first, then attach or add its websites.</p>
            ) : (
              <HostedSitesEditor key={editing.id} account={editing} allSites={sites} onChanged={reloadSites} />
            )}
          </div>
        </div>
      </SidePanel>
    </div>
  );
}

/** One account on a phone: name and status, its sites, then last run, price and the row actions. */
function AccountCard({
  account: a,
  first,
  client,
  sites,
  run,
  openFixes,
  onOpen,
  actions,
}: {
  account: HostingAccount;
  first: boolean;
  client: string | null;
  sites: HostedSite[];
  run: HostingRun | undefined;
  openFixes: Record<string, AiJob>;
  onOpen: () => void;
  actions: React.ReactNode;
}) {
  return (
    <div
      role="link"
      tabIndex={0}
      className="hover-wash"
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen();
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '12px 12px 12px 14px',
        borderTop: first ? 'none' : '1px solid var(--border-hairline)',
        cursor: 'pointer',
        ...(sites.some((s) => openFixes[s.id]) ? OPEN_FIX_WASH : null),
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: 'var(--text-body)', fontWeight: 500, overflowWrap: 'anywhere' }}>{a.name}</div>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
            {client ?? 'No client attached'}
            {siteCount(sites)}
          </div>
        </div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <span className="t-num" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
            {money(a.monthly_price)}
          </span>
          <Badge tone={STATUS_TONE[a.status]} dot>
            {HOSTING_STATUS_LABEL[a.status]}
          </Badge>
        </span>
      </div>
      {sites.length ? <SiteList sites={sites} openFixes={openFixes} compact /> : null}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
        {run ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <RunStatusBadge status={run.status} reviewed={Boolean(run.reviewed_at)} />
            <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
              {timeAgo(run.finished_at ?? run.started_at ?? run.created_at)}
            </span>
          </span>
        ) : (
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>No runs yet</span>
        )}
        <span style={{ marginLeft: 'auto' }}>{actions}</span>
      </div>
    </div>
  );
}

/** " · 3 sites" after the client name, so a multi-site account reads as one at a glance. */
const siteCount = (sites: HostedSite[]) => (sites.length > 1 ? <span className="t-num"> · {sites.length} sites</span> : null);

/**
 * An account's sites in a list cell: the first SITES_SHOWN hosts (those with an open fix
 * first, so their badge shows), then "+N more" naming the rest on hover. The table gives
 * each host a line; the phone card runs them together.
 */
function SiteList({ sites, openFixes, compact = false }: { sites: HostedSite[]; openFixes: Record<string, AiJob>; compact?: boolean }) {
  if (!sites.length) return <span style={{ color: 'var(--text-faint)' }}>None</span>;
  const ordered = [...sites].sort((x, y) => Number(Boolean(openFixes[y.id])) - Number(Boolean(openFixes[x.id])));
  const shown = ordered.slice(0, SITES_SHOWN);
  const rest = ordered.slice(SITES_SHOWN);
  const restFixes = rest.filter((s) => openFixes[s.id]).length;
  return (
    <span style={{ display: 'flex', flexDirection: compact ? 'row' : 'column', flexWrap: compact ? 'wrap' : 'nowrap', gap: compact ? '2px 12px' : '3px', alignItems: compact ? 'center' : 'flex-start', minWidth: 0 }}>
      {shown.map((s) => (
        <span key={s.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', minWidth: 0, maxWidth: '100%', flexWrap: compact ? 'wrap' : 'nowrap' }}>
          <Link
            to={`/uptime/${s.id}`}
            onClick={(e) => e.stopPropagation()}
            className={compact ? 't-num' : undefined}
            title={host(s.url)}
            style={{
              color: 'var(--text-muted)',
              fontSize: compact ? 'var(--text-xs)' : 'var(--text-sm)',
              minWidth: 0,
              ...(compact ? { overflowWrap: 'anywhere' } : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
            }}
          >
            {host(s.url)}
          </Link>
          <OpenFixBadge job={openFixes[s.id]} />
        </span>
      ))}
      {rest.length ? (
        <span
          title={rest.map((s) => host(s.url) + (openFixes[s.id] ? ' (fix in the agent queue)' : '')).join('\n')}
          style={{ fontSize: 'var(--text-xs)', color: restFixes ? 'var(--status-warn)' : 'var(--text-faint)', whiteSpace: 'nowrap', cursor: 'default' }}
        >
          +{rest.length} more{restFixes ? ` · ${restFixes} with a fix` : ''}
        </span>
      ) : null}
    </span>
  );
}

/** Run queues the checklist with updates; the reason it can't sits on the wrapper (a disabled button swallows hover). */
function RunCell({ sites, live, busy, onRun }: { sites: HostedSite[]; live: boolean; busy: boolean; onRun: () => void }) {
  const runnable = sites.filter(isRunnableSite);
  const reason = !runnable.length
    ? sites.length
      ? `No Cloudways or Netlify site linked (${plural(sites.length, 'site')} on the account)`
      : 'No websites on this account'
    : live
      ? 'A run is already queued or running'
      : undefined;
  // Unlinked sites are skipped by the run, so say which when it isn't all of them.
  const skipped = sites.filter((s) => !isRunnableSite(s));
  const what =
    sites.length > 1
      ? `on ${runnable.length === sites.length ? `all ${sites.length} sites` : `${runnable.length} of ${sites.length} sites (${skipped.map((s) => host(s.url)).join(', ')} not linked)`}`
      : '';
  return (
    <span title={reason ?? `Run the managed hosting checklist ${what ? `${what}, ` : ''}updates included`} style={{ display: 'inline-flex' }}>
      <Button size="sm" variant="secondary" icon="play" disabled={Boolean(reason) || busy} onClick={onRun}>
        {busy ? 'Queuing…' : 'Run'}
      </Button>
    </span>
  );
}

/** Send fix queues the latest run's hosting fix; why it can't sits on the wrapper, like Run. */
function FixCell({
  pending,
  live,
  hasRun,
  inQueue,
  signedIn,
  busy,
  onSend,
}: {
  pending: PendingFix | undefined;
  live: boolean;
  hasRun: boolean;
  inQueue: boolean;
  signedIn: boolean;
  busy: boolean;
  onSend: () => void;
}) {
  const reason = pending
    ? signedIn
      ? undefined
      : 'Sign in to send fixes'
    : live
      ? 'Wait for the run to finish'
      : !hasRun
        ? 'No checklist run yet'
        : inQueue
          ? 'A hosting fix is already in the agent queue'
          : 'Nothing in the last run for an agent to fix';
  const count = pending ? pending.items.filter((i) => i.owner === 'agent' && pending.sites.some((s) => s.id === i.site_id)).length : 0;
  const jobs = pending?.sites.length ?? 0;
  // One job per site: with several, say so and how many fixes each carries.
  const perSite = pending?.sites.map((s) => `${host(s.url)} (${plural(pending.items.filter((i) => i.owner === 'agent' && i.site_id === s.id).length, 'fix', 'fixes')})`).join(', ');
  const title =
    reason ??
    (jobs > 1
      ? `Send ${plural(count, 'fix', 'fixes')} from the last run to the agent queue as ${jobs} jobs, one per site: ${perSite}`
      : `Send ${plural(count, 'fix', 'fixes')} from the last run to the agent queue (${perSite})`);
  return (
    <span title={title} style={{ display: 'inline-flex' }}>
      <Button size="sm" variant="secondary" icon="sparkles" disabled={Boolean(reason) || busy} onClick={onSend}>
        {busy ? 'Sending…' : jobs > 1 ? `Send ${jobs} fixes` : 'Send fix'}
      </Button>
    </span>
  );
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function ErrorNote({ text }: { text: string }) {
  return (
    <div
      style={{
        padding: '10px 12px',
        borderRadius: 'var(--radius-md)',
        background: 'var(--status-down-soft)',
        color: 'var(--status-down)',
        fontSize: 'var(--text-sm)',
      }}
    >
      {text}
    </div>
  );
}
